import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FilterSelect } from '../components/charts/FilterControls';
import DashboardLayout from '../components/DashboardLayout';
import { ModelNameWithDetail, RoutedModelDetail } from '../components/ModelNameWithDetail';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const TIER_LABELS = {
  equal: 'Equal',
  better: 'Better',
  cost_effective: 'Cost effective',
};

const TIMEFRAMES = [
  { id: 'day', label: '1D' },
  { id: 'week', label: '7D' },
  { id: 'month', label: '30D' },
  { id: 'year', label: '1Y' },
];

const formatLatency = (ms) => {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatSavings = (pct) => {
  if (pct == null) return null;
  if (pct >= 1) return `${Math.round(pct)}% cheaper`;
  if (pct <= -1) return `${Math.round(-pct)}% pricier`;
  return 'same price';
};

const formatQuality = (pct) => {
  if (pct == null) return null;
  if (pct > 0) return `+${pct}% quality`;
  if (pct < 0) return `${pct}% quality`;
  return 'same quality';
};

function RoutesTo({ inputModel, tier, mapping }) {
  if (!mapping?.os_model) {
    return <span className="opacity-50">No replacement model</span>;
  }
  return <RoutedModelDetail compact model={inputModel} tier={tier} mapping={mapping} />;
}

export default function RoutingPage() {
  const { selectedHash, selectedProject } = useProject();
  const navigate = useNavigate();
  const { inputModel: inputModelParam } = useParams();
  const [searchParams] = useSearchParams();
  const [rules, setRules] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [timeframe, setTimeframe] = useState('month');
  const [apiKey, setApiKey] = useState('');
  const [selectedTier, setSelectedTier] = useState('equal');
  const [routePercent, setRoutePercent] = useState(100);
  const [routeEnabled, setRouteEnabled] = useState(true);
  const [doEvals, setDoEvals] = useState(true);

  const inputModel = inputModelParam ? decodeURIComponent(inputModelParam) : null;
  const editingRule = useMemo(
    () => (inputModel ? rules.find((r) => r.input_model === inputModel) : null),
    [inputModel, rules],
  );

  const loadRules = () => {
    if (!selectedHash) return;
    dashboardService.routing(
      selectedHash,
      { timeframe, api_key: apiKey },
      (res) => {
        setRules(res.data.rules || []);
        setApiKeys(res.data.filter_options?.keys || []);
      },
      console.error,
    );
  };

  useEffect(() => {
    loadRules();
  }, [selectedHash, timeframe, apiKey]);

  useEffect(() => {
    if (!inputModel || !rules.length) return;
    if (!editingRule) {
      navigate('/routing/', { replace: true });
      return;
    }
    const suggestedTier = searchParams.get('suggested_tier');
    setSelectedTier(
      suggestedTier && editingRule.available_tiers?.includes(suggestedTier) ? suggestedTier : editingRule.tier,
    );
    setRoutePercent(editingRule.route_percent ?? 100);
    setRouteEnabled(editingRule.enabled ?? true);
    setDoEvals(editingRule.do_evals ?? true);
  }, [inputModel, editingRule, rules.length, searchParams, navigate]);

  const closeSettings = () => navigate('/routing/');

  const toggle = (model, enabled) => {
    dashboardService.updateRouting(
      selectedHash,
      { input_model: model, enabled },
      loadRules,
      console.error,
    );
  };

  const openSettings = (rule) => {
    navigate(`/routing/${encodeURIComponent(rule.input_model)}/`);
  };

  const saveTier = () => {
    if (!editingRule) return;
    dashboardService.updateRouting(
      selectedHash,
      {
        input_model: editingRule.input_model,
        tier: selectedTier,
        route_percent: routePercent,
        enabled: routeEnabled,
        do_evals: doEvals,
      },
      () => {
        closeSettings();
        loadRules();
      },
      console.error,
    );
  };

  return (
    <DashboardLayout title="Routing">
      <p className="text-sm opacity-70 mb-4">Models detected from your traffic and where we route them.</p>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm opacity-60">Period</span>
          <div className="flex rounded-xl border border-theme-secondary bg-theme-secondary/40 p-1">
            {TIMEFRAMES.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTimeframe(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  timeframe === opt.id
                    ? 'bg-theme-main text-theme-primary shadow-sm'
                    : 'text-theme-text opacity-70 hover:opacity-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <FilterSelect
          label="API key"
          value={apiKey}
          onChange={setApiKey}
          options={apiKeys.map((k) => ({ value: k.hash, label: k.name }))}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left opacity-60 border-b border-theme-secondary">
              <th className="py-2">Input model</th>
              <th className="py-2">Requests</th>
              <th className="py-2">Avg latency</th>
              <th className="py-2">Routes to</th>
              <th className="py-2">Savings</th>
              <th className="py-2">Passthrough %</th>
              <th className="py-2">Cache %</th>
              <th className="py-2">Enabled</th>
              <th className="py-2 w-10" aria-label="Settings" />
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.input_model} className="border-b border-theme-secondary/50">
                <td className="py-3">
                  <ModelNameWithDetail compact model={r.input_model} tier={r.tier} emphasis="original" />
                </td>
                <td className="py-3">{r.request_count}</td>
                <td className="py-3 font-mono text-xs">{formatLatency(r.avg_latency_ms)}</td>
                <td className="py-3"><RoutesTo inputModel={r.input_model} tier={r.tier} mapping={r.mapping} /></td>
                <td className="py-3">{r.mapping?.savings_pct != null ? `${r.mapping.savings_pct}%` : '—'}</td>
                <td className="py-3">{r.passthrough_rate != null ? `${r.passthrough_rate}%` : '—'}</td>
                <td className="py-3">{r.cache_rate != null ? `${r.cache_rate}%` : '—'}</td>
                <td className="py-3">
                  <button
                    type="button"
                    onClick={() => toggle(r.input_model, !r.enabled)}
                    className={`px-3 py-1 rounded-full text-xs ${r.enabled ? 'bg-theme-primary text-theme-button' : 'bg-theme-secondary opacity-60'}`}
                  >
                    {r.enabled ? 'On' : 'Off'}
                  </button>
                </td>
                <td className="py-3">
                  <button
                    type="button"
                    onClick={() => openSettings(r)}
                    className="p-1 rounded text-theme-text opacity-50 hover:opacity-100 hover:text-theme-primary"
                    aria-label={`Settings for ${r.input_model}`}
                  >
                    ⚙
                  </button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr>
                <td colSpan={9} className="py-8 text-center opacity-50">
                  No models used in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingRule && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeSettings}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-medium mb-1">Routing settings</h2>
            <p className="text-sm opacity-70 mb-4 font-mono">{editingRule.input_model}</p>
            <div className="flex items-center justify-between mb-4 px-4 py-3 rounded-lg border border-theme-secondary">
              <div>
                <div className="font-medium">Route this model</div>
                <div className="text-xs opacity-60 mt-0.5">
                  {routeEnabled ? 'Sending matching traffic to an open-source model.' : 'Passing all traffic through to your provider.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRouteEnabled((on) => !on)}
                className={`px-3 py-1 rounded-full text-xs ${routeEnabled ? 'bg-theme-primary text-theme-button' : 'bg-theme-secondary opacity-60'}`}
              >
                {routeEnabled ? 'On' : 'Off'}
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {editingRule.available_tiers?.length > 0 ? (
                editingRule.available_tiers.map((tier) => {
                  const option = editingRule.tier_options?.[tier];
                  const metrics = [
                    formatSavings(option?.savings_pct),
                    formatQuality(option?.quality_delta_pct),
                    option?.tps != null ? `${option.tps} tps` : null,
                  ].filter(Boolean);
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setSelectedTier(tier)}
                      className={`w-full text-left px-4 py-3 rounded-lg border ${
                        selectedTier === tier
                          ? 'border-theme-primary bg-theme-secondary/50'
                          : 'border-theme-secondary'
                      }`}
                    >
                      <div className="font-medium">{TIER_LABELS[tier]}</div>
                      {option?.os_model && (
                        <div className="text-xs opacity-60 font-mono mt-0.5">
                          {option.upstream_provider} · {option.os_model}
                        </div>
                      )}
                      {metrics.length > 0 && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-80 mt-1.5">
                          {metrics.map((m) => (
                            <span key={m}>{m}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="text-sm opacity-60">No replacement tiers are available for this model yet.</p>
              )}
            </div>
            <label className="block text-sm mb-2">Route % of requests</label>
            <input
              type="range"
              min="0"
              max="100"
              value={routePercent}
              onChange={(e) => setRoutePercent(Number(e.target.value))}
              className="w-full mb-4"
            />
            <div className="text-sm opacity-70 mb-6">{routePercent}%</div>
            {selectedProject?.eval_enabled && (
              <label className="flex items-center justify-between mb-6 px-4 py-3 rounded-lg border border-theme-secondary text-sm">
                <span>Include in shadow evals</span>
                <input type="checkbox" checked={doEvals} onChange={(e) => setDoEvals(e.target.checked)} />
              </label>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeSettings}
                className="px-4 py-2 rounded-lg border border-theme-secondary text-sm opacity-80 hover:opacity-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTier}
                className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
